<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Button from '../../Button.svelte';
  import { DonationService, type DonationRequest } from '../../../lib/api/admin/donation.service';
  import { EmployerProfileService } from '../../../lib/api/admin/employer-profile.service';
  import { employersService } from '../../../lib/api/admin/employers.service';
  import Notification from '../../Notification.svelte';
  import BackButton from '../../BackButton.svelte';
  import Breadcrumbs from '../../Breadcrumbs.svelte';
  import Stepper from '../../Stepper.svelte';
  import StageBadge from '../../StageBadge.svelte';
  import { stageFromStatusName, type LifecycleStage } from '../../../lib/donationLifecycle';
  import LoadingSkeleton from '../../LoadingSkeleton.svelte';
  import ErrorState from '../../ErrorState.svelte';

  export let params: { id: string };
  
  let loading = false;
  let error: string | null = null;
  let donationData: DonationRequest | null = null;
  let resendingInvoice = false;
  let showReassignModal = false;
  let reassignSearch = '';
  let reassignEmployerUuid = '';
  let reassignEmployerOptions: Array<{ uuid: string; name: string }> = [];
  let reassignLoading = false;
  let reassignSearching = false;

  // Notification state
  let notificationShow = false;
  let notificationType: 'success' | 'error' = 'success';
  let notificationMessage = '';

  function showNotification(message: string, type: 'success' | 'error' = 'success') {
    notificationMessage = message;
    notificationType = type;
    notificationShow = true;
    setTimeout(() => notificationShow = false, 4000);
  }

  function isDonationRequest(value: unknown): value is DonationRequest {
    return (
      typeof value === 'object' &&
      value !== null &&
      'uuid' in value &&
      'leave_hours' in value &&
      !('items' in value)
    );
  }

  function extractDonationRequest(data: DonationRequest | DonationRequest[] | { items: DonationRequest[] } | undefined): DonationRequest | null {
    if (!data) return null;
    if (Array.isArray(data)) return data[0] ?? null;
    if ('items' in data) return data.items[0] ?? null;
    if (isDonationRequest(data)) return data;
    return null;
  }

  async function loadDonationDetails() {
    loading = true;
    error = null;
    try {
      const response = await DonationService.getDonationsbyUuid(params.id);
      if (response.success && response.data) {
        donationData = extractDonationRequest(response.data);
      } else {
        error = 'Failed to load donation details';
        showNotification(error, 'error');
      }
    } catch (err: any) {
      error = err.message || 'Failed to load donation details';
      showNotification(error ?? 'Failed to load donation details', 'error');
    } finally {
      loading = false;
    }
  }

  let showDeleteModal = false;
  let deleting = false;

  async function handleDeleteDonation() {
    if (!params?.id) return;
    deleting = true;
    try {
      const result = await DonationService.deleteDonation(params.id);
      if (result.success) {
        showNotification('Donation deleted successfully', 'success');
        push('/donation-list');
      } else {
        showNotification(result.message || 'Failed to delete donation', 'error');
      }
    } catch (err: any) {
      showNotification(err.message || 'Failed to delete donation', 'error');
    } finally {
      deleting = false;
      showDeleteModal = false;
    }
  }

  async function handleResendInvoiceEmail() {
    if (!params?.id) return;
    resendingInvoice = true;
    try {
      const result = await DonationService.resendReceipt(params.id);
      if (result.success) {
        showNotification(result.message || 'Invoice email resent to employee', 'success');
      } else {
        showNotification(result.message || 'Failed to resend invoice email', 'error');
      }
    } catch (err: any) {
      showNotification(err.message || 'Failed to resend invoice email', 'error');
    } finally {
      resendingInvoice = false;
    }
  }

  function handleBack() {
    push('/donation-list');
  }

  function formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';
    return DonationService.formatDate(dateString);
  }

  function formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) return '$0.00';
    return DonationService.formatCurrency(value);
  }

  // Create timeline items from donation data
  function getTimelineItems() {
    if (!donationData) return [];
    
    const items: Array<{ event: string; date: string | null; description: string; icon: string }> = [];
    
    // Created event
    if (donationData.created_at) {
      items.push({
        event: 'Created',
        date: donationData.created_at,
        description: 'Donation request was created',
        icon: 'created'
      });
    }
    
    // Approved event
    if (donationData.approved_at) {
      items.push({
        event: 'Approved',
        date: donationData.approved_at,
        description: 'Donation request was approved',
        icon: 'approved'
      });
    }
    
    // Rejected event
    if (donationData.rejected_at) {
      items.push({
        event: 'Rejected',
        date: donationData.rejected_at,
        description: donationData.rejection_reason || 'Donation request was rejected',
        icon: 'rejected'
      });
    }

    if (donationData.charity_received_at) {
      items.push({
        event: 'Received by charity',
        date: donationData.charity_received_at,
        description: 'Charity confirmed the donation was received into their account',
        icon: 'received'
      });
    }
    
    // Updated event (if different from created/approved/rejected)
    if (donationData.updated_at && donationData.updated_at !== donationData.created_at) {
      const isDuplicate = 
        donationData.updated_at === donationData.approved_at ||
        donationData.updated_at === donationData.rejected_at;
      
      if (!isDuplicate) {
        items.push({
          event: 'Updated',
          date: donationData.updated_at,
          description: 'Donation request was updated',
          icon: 'updated'
        });
      }
    }
    
    // Sort by date (most recent first)
    return items.sort((a, b) => {
      if (!a.date) return 1;
      if (!b.date) return -1;
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }

  // Breadcrumbs items
  $: breadcrumbsItems = [
    { label: 'Admin', href: '/admin-dashboard' },
    { label: 'Donation List', href: '/donation-list' },
    { label: donationData ? `Donation #${donationData.uuid.substring(0, 8)}` : 'Donation Details' }
  ];

  async function searchReassignEmployers() {
    if (!reassignSearch.trim()) {
      reassignEmployerOptions = [];
      return;
    }
    reassignSearching = true;
    try {
      const result = await employersService.getEmployers({
        phrase: reassignSearch.trim(),
        per_page: 10,
      });
      reassignEmployerOptions = (result.data ?? []).map((e) => ({
        uuid: e.uuid,
        name: e.name,
      }));
    } catch {
      reassignEmployerOptions = [];
    } finally {
      reassignSearching = false;
    }
  }

  async function handleReassignEmployer() {
    if (!params?.id || !reassignEmployerUuid.trim()) {
      showNotification('Select an employer to reassign', 'error');
      return;
    }
    reassignLoading = true;
    try {
      const result = await EmployerProfileService.reassignDonationEmployer(
        params.id,
        reassignEmployerUuid.trim()
      );
      if (result.success) {
        showNotification(result.message || 'Employer reassigned', 'success');
        showReassignModal = false;
        reassignEmployerUuid = '';
        reassignSearch = '';
        await loadDonationDetails();
      } else {
        showNotification(result.message || 'Failed to reassign employer', 'error');
      }
    } catch (err: any) {
      showNotification(err.message || 'Failed to reassign employer', 'error');
    } finally {
      reassignLoading = false;
    }
  }

  function canResendInvoiceEmail(donation: DonationRequest): boolean {
    const statusName = donation.donation_request_status?.name?.toUpperCase();
    return statusName === 'COMPLETED' || statusName === 'PROCESSED';
  }

  // A donation can only be deleted before funds have reached the charity.
  function canDeleteDonation(currentStage: LifecycleStage): boolean {
    return currentStage !== 'paid_to_charity' && currentStage !== 'received_by_charity';
  }

  // Canonical lifecycle stage for the progress stepper.
  $: stage = (donationData
    ? ((donationData.lifecycle_stage as LifecycleStage) || stageFromStatusName(donationData.donation_request_status?.name))
    : 'submitted') as LifecycleStage;

  onMount(() => {
    if (params.id) {
      loadDonationDetails();
    } else {
      error = 'No donation ID provided';
      showNotification(error, 'error');
    }
  });
</script>

<Notification 
  bind:show={notificationShow}
  type={notificationType}
  message={notificationMessage}
/>

<div class="view-donation-page">
  <Breadcrumbs items={breadcrumbsItems} />
  <div class="back-button-row">
    <BackButton text="Back to Donation List" onClick={handleBack} />
  </div>

  {#if loading}
    <div style="min-height: 400px; padding: 2rem;">
      <LoadingSkeleton lines={8} height="2rem" />
    </div>
  {:else if error}
    <ErrorState 
      message="Failed to load donation details" 
      description={error}
      onRetry={loadDonationDetails}
    />
  {:else if donationData}
    <!-- Header Section with progress stepper -->
    <div class="header-section">
      <div class="header-content">
        <h1>Donation Details</h1>
        <StageBadge {stage} />
      </div>
      <!-- Canonical progress stepper -->
      <div class="stepper-wrapper">
        <Stepper {stage} />
      </div>
    </div>

    <!-- Actions Section -->
    {#if canResendInvoiceEmail(donationData) || canDeleteDonation(stage)}
      <div class="info-section actions-section">
        <h2>Actions</h2>
        <div class="actions-bar">
          {#if canResendInvoiceEmail(donationData)}
            <Button variant="secondary" on:click={handleResendInvoiceEmail} disabled={resendingInvoice}>
              {resendingInvoice ? 'Sending...' : 'Resend Invoice Email'}
            </Button>
          {/if}
          {#if canDeleteDonation(stage)}
            <Button variant="danger" on:click={() => showDeleteModal = true}>
              Delete Donation
            </Button>
          {/if}
        </div>
      </div>
    {/if}

    <!-- Donation Information Section -->
    <div class="info-section">
      <h2>Donation Information</h2>
      <div class="info-grid">
        <div class="info-item">
          <span class="info-label">Leave Hours</span>
          <span>{donationData.leave_hours || 0} hrs</span>
        </div>
        <div class="info-item">
          <span class="info-label">Employee Value</span>
          <span>{formatCurrency(donationData.leave_value)}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Employer Contribution</span>
          <span>{formatCurrency(donationData.employer_top_up)}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Total Donation</span>
          <span class="total-value">{formatCurrency(donationData.total_donation || ((donationData.leave_value || 0) + (donationData.employer_top_up || 0)))}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Request Date</span>
          <span>{formatDate(donationData.created_at)}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Donation ID</span>
          <span class="uuid-text">{donationData.uuid}</span>
        </div>
        {#if donationData.charity_received_at}
          <div class="info-item">
            <span class="info-label">Charity confirmed receipt</span>
            <span>{formatDate(donationData.charity_received_at)}</span>
          </div>
        {/if}
      </div>
      {#if donationData.rejection_reason}
        <div class="rejection-note">
          <span class="info-label">Rejection Reason</span>
          <p>{donationData.rejection_reason}</p>
        </div>
      {/if}
      {#if donationData.employer_comment}
        <div class="employer-comment">
          <span class="info-label">Employer Comment</span>
          <p>{donationData.employer_comment}</p>
        </div>
      {/if}
    </div>

    <!-- Employee Details Section -->
    <div class="info-section">
      <h2>Employee Details</h2>
      <div class="info-grid">
        <div class="info-item">
          <span class="info-label">Employee Name</span>
          <span>{donationData.user?.full_name || 'N/A'}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Email</span>
          <span>{donationData.user?.email || 'N/A'}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Job Title</span>
          <span>{donationData.user?.job_title || 'N/A'}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Department</span>
          <span>{donationData.user?.department || 'N/A'}</span>
        </div>
      </div>
    </div>

    <!-- Employer Details Section -->
    {#if donationData.employer}
      <div class="info-section">
        <div class="section-header-row">
          <h2>Employer Details</h2>
          <Button variant="secondary" on:click={() => showReassignModal = true}>
            Reassign employer
          </Button>
        </div>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Employer Name</span>
            <span>{donationData.employer.name}</span>
          </div>
          <div class="info-item">
            <span class="info-label">ABN</span>
            <span>{donationData.employer.abn || 'N/A'}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Contact Email</span>
            <span>{donationData.employer.contact_email || 'N/A'}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Contact Phone</span>
            <span>{donationData.employer.contact_phone || 'N/A'}</span>
          </div>
        </div>
      </div>
    {/if}

    <!-- Charity Information Section -->
    {#if donationData.charity}
    <div class="info-section">
      <h2>Charity Information</h2>
      <div class="info-grid">
        <div class="info-item">
          <span class="info-label">Charity Name</span>
          <span>{donationData.charity.name}</span>
        </div>
        <div class="info-item">
          <span class="info-label">ABN</span>
            <span>{donationData.charity.abn || donationData.charity.charity_government_id || 'N/A'}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Category</span>
            <span>{donationData.charity.category?.name || 'N/A'}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Website</span>
            <span>
              {#if donationData.charity.website}
                <a href={donationData.charity.website} target="_blank" rel="noopener noreferrer">
                  {donationData.charity.website}
                </a>
              {:else}
                N/A
              {/if}
            </span>
          </div>
        </div>
      </div>
    {/if}

    <!-- Timeline/History Section -->
    <div class="info-section timeline-section">
      <h2>Status History</h2>
      <div class="timeline">
        {#each getTimelineItems() as item, index}
          <div class="timeline-item">
            <div class="timeline-marker {item.icon}"></div>
            <div class="timeline-content">
              <div class="timeline-header">
                <span class="timeline-event">{item.event}</span>
                <span class="timeline-date">{formatDate(item.date)}</span>
              </div>
              <p class="timeline-description">{item.description}</p>
      </div>
    </div>
        {/each}
      </div>
      </div>
  {/if}

  {#if showReassignModal}
    <div class="modal-overlay" role="presentation" on:click|self={() => showReassignModal = false} on:keydown={(e) => e.key === 'Escape' && (showReassignModal = false)}>
      <div class="modal-content reassign-modal" role="dialog" aria-modal="true" aria-labelledby="reassign-employer-title">
        <h3 id="reassign-employer-title">Reassign donation employer</h3>
        <p class="modal-help">Search for the correct employer record. Donations will point to the selected employer.</p>
        <label for="reassign-search">Search employers</label>
        <input
          id="reassign-search"
          type="text"
          class="reassign-input"
          bind:value={reassignSearch}
          on:input={searchReassignEmployers}
          placeholder="Employer name"
        />
        {#if reassignSearching}
          <p class="modal-help">Searching…</p>
        {:else if reassignEmployerOptions.length > 0}
          <ul class="employer-pick-list">
            {#each reassignEmployerOptions as option}
              <li>
                <button
                  type="button"
                  class:selected={reassignEmployerUuid === option.uuid}
                  on:click={() => reassignEmployerUuid = option.uuid}
                >
                  {option.name}
                </button>
              </li>
            {/each}
          </ul>
        {/if}
        <label for="reassign-uuid">Or paste employer UUID</label>
        <input id="reassign-uuid" type="text" class="reassign-input" bind:value={reassignEmployerUuid} />
        <div class="modal-actions">
          <Button variant="secondary" on:click={() => showReassignModal = false} disabled={reassignLoading}>Cancel</Button>
          <Button variant="primary" on:click={handleReassignEmployer} disabled={reassignLoading}>
            {reassignLoading ? 'Saving…' : 'Reassign'}
          </Button>
        </div>
      </div>
    </div>
  {/if}

  {#if showDeleteModal}
    <div class="modal-overlay" role="presentation" on:click|self={() => showDeleteModal = false} on:keydown={(e) => e.key === 'Escape' && (showDeleteModal = false)}>
      <div class="modal-content" role="dialog" aria-modal="true" aria-labelledby="delete-donation-title">
        <h3 id="delete-donation-title">Delete Donation</h3>
        <p>Are you sure you want to delete this donation request? This action cannot be undone.</p>
        <div class="modal-actions">
          <Button variant="secondary" on:click={() => showDeleteModal = false} disabled={deleting}>Cancel</Button>
          <Button variant="danger" on:click={handleDeleteDonation} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .view-donation-page {
    padding: 1.5rem 2rem;
    background: #f7fafc;
  }

  .back-button-row {
    margin: 0;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid #f0b429;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-container {
    padding: 2rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .error-message {
    color: #dc2626;
    margin-bottom: 1rem;
    font-size: 1rem;
  }

  .header-section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .section-header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .section-header-row h2 {
    margin: 0;
  }

  .reassign-input {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    margin: 0.5rem 0 1rem;
    box-sizing: border-box;
  }

  .modal-help {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0 0 0.75rem;
  }

  .employer-pick-list {
    list-style: none;
    padding: 0;
    margin: 0 0 1rem;
    max-height: 200px;
    overflow-y: auto;
  }

  .employer-pick-list button {
    width: 100%;
    text-align: left;
    padding: 0.5rem 0.75rem;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: white;
    margin-bottom: 0.5rem;
    cursor: pointer;
  }

  .employer-pick-list button.selected {
    border-color: var(--color-primary, #ffb400);
    background: #fff8e1;
  }

  .reassign-modal {
    max-width: 520px;
  }

  .header-content h1 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .actions-bar {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .stepper-wrapper {
    margin-top: 1.5rem;
  }

  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
  }

  .modal-content {
    background-color: #fff;
    border-radius: 10px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    max-width: 450px;
    width: 90%;
    padding: 2rem;
  }

  .modal-content h3 {
    margin: 0 0 1rem 0;
    font-size: 1.25rem;
    color: #991b1b;
  }

  .modal-content p {
    color: #374151;
    margin: 0 0 1.5rem 0;
    line-height: 1.6;
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
  }

  .info-section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .info-section h2 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 1.5rem 0;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .info-item .info-label {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    color: #6b7280;
    font-weight: 500;
  }

  .info-item span {
    font-size: 0.875rem;
    color: #111827;
    font-weight: 500;
  }

  .info-item .total-value {
    font-size: 1rem;
    font-weight: 600;
    color: #059669;
  }

  .info-item .uuid-text {
    font-family: monospace;
    font-size: 0.75rem;
    color: #6b7280;
  }

  .rejection-note,
  .employer-comment {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e5e7eb;
  }

  .rejection-note .info-label,
  .employer-comment .info-label {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    color: #6b7280;
    font-weight: 500;
    display: block;
    margin-bottom: 0.5rem;
  }

  .rejection-note p,
  .employer-comment p {
    font-size: 0.875rem;
    color: #111827;
    margin: 0;
    padding: 0.75rem;
    background: #f9fafb;
    border-radius: 8px;
  }

  .rejection-note p {
    color: #dc2626;
  }

  .info-item a {
    color: #3b82f6;
    text-decoration: none;
  }

  .info-item a:hover {
    text-decoration: underline;
  }

  /* Timeline Styles */
  .timeline-section {
    padding: 1.5rem;
  }

  .timeline {
    position: relative;
    padding-left: 2rem;
  }

  .timeline::before {
    content: '';
    position: absolute;
    left: 0.5rem;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #e5e7eb;
  }

  .timeline-item {
    position: relative;
    padding-bottom: 1.5rem;
    display: flex;
    align-items: flex-start;
  }

  .timeline-item:last-child {
    padding-bottom: 0;
  }

  .timeline-marker {
    position: absolute;
    left: -1.75rem;
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 50%;
    background: #d1d5db;
    border: 2px solid white;
    box-shadow: 0 0 0 2px #e5e7eb;
    z-index: 1;
  }

  /* Markers use the canonical status palette (TONE_PALETTE in src/lib/donationLifecycle.ts):
     created = submitted (yellow), approved (orange), received = received-by-charity (green),
     rejected (red); updated is not a lifecycle status, so it stays neutral grey. */
  .timeline-marker.created {
    background: #eab308;
    box-shadow: 0 0 0 2px #fef9c3;
  }

  .timeline-marker.approved {
    background: #f97316;
    box-shadow: 0 0 0 2px #ffedd5;
  }

  .timeline-marker.received {
    background: #22c55e;
    box-shadow: 0 0 0 2px #d1fae5;
  }

  .timeline-marker.rejected {
    background: #ea4335;
    box-shadow: 0 0 0 2px #fee2e2;
  }

  .timeline-marker.updated {
    background: #9ca3af;
    box-shadow: 0 0 0 2px #f3f4f6;
  }

  .timeline-content {
    flex: 1;
  }

  .timeline-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.25rem;
  }

  .timeline-event {
    font-size: 0.875rem;
    font-weight: 600;
    color: #111827;
  }

  .timeline-date {
    font-size: 0.75rem;
    color: #6b7280;
  }

  .timeline-description {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
  }

  @media (max-width: 768px) {
    .view-donation-page {
      padding: 1rem;
    }

    .header-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
    }

    .info-grid {
      grid-template-columns: 1fr;
    }

    .timeline {
      padding-left: 1.5rem;
    }

    .timeline-marker {
      left: -1.25rem;
    }
  }
</style>